import { startTransition, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@xiltrion/ui/collapsible';
import { Field, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { InputClave } from '@xiltrion/ui/input-clave';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@xiltrion/ui/input-otp';
import { apiGet, apiPost, API_BASE, errorMessage } from '@/api/client';
import { entityFromHost } from '@/app/entity-hint';
import { entry } from '@/app/session';
import { avisarIngreso } from './sigue-otra-pestana';

type Providers = { local: boolean; federated: { id: string; name: string; entity: string; kind: string } | null };

/**
 * Lo que envuelve el formulario de ingreso (la cortina de la portada, o su lado derecho sin entidad). Es una función
 * que se llama, no un componente, para que el formulario no se remonte (y pierda lo escrito) en cada render.
 */
export type MarcoIngreso = (p: { titulo: string; subtitulo?: string; children: ReactNode }) => ReactNode;

/**
 * Formulario de ingreso: correo y contraseña juntos; al salir del correo se consulta si su entidad usa Entra/Google.
 * Al terminar va a `volver`. Si el destino es el Inicio (con o sin `?app=`), deja la marca de «acaba de ingresar»
 * para que el Inicio decida si salta a una app.
 */
export function FormularioIngreso({ volver, titulo, subtitulo, marco, aviso }: { volver: string; titulo: string; subtitulo?: string; marco: MarcoIngreso; /** Aviso sobre el formulario (p. ej. el error con que una app devolvió a la portada). */ aviso?: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [olvide, setOlvide] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [checked, setChecked] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { data: providers } = useQuery({ queryKey: ['providers', checked], queryFn: () => apiGet<Providers>('/auth/providers', checked ? { email: checked } : undefined), placeholderData: (prev) => prev });
  // Al salir del correo con Tab. La consulta se pide en una transición para que el render no ocurra dentro del
  // `blur`: en ese instante nadie tiene el foco todavía y, como el rerender recrea el `<path>` del ícono del ojo, el
  // `FocusScope` del diálogo lo tomaba por «se borró lo enfocado» y se quedaba con el foco antes de que llegara a la contraseña.
  const checkEmail = () => {
    const value = email.trim().toLowerCase();
    if (value.includes('@')) startTransition(() => setChecked(value));
  };
  const alInicio = new URL(volver, window.location.origin).pathname === '/';
  const markEntry = () => { if (alInicio) entry.mark(); };
  // En un dominio propio la sesión tiene que quedar en el host canónico (el SSO de las apps vive allí): el ingreso
  // federado va directo al API del canónico, y el local salta una vez con un código de un solo uso (`/auth/handoff`).
  const startFederated = async (idp: string) => {
    markEntry();
    const propio = await entityFromHost();
    window.location.href = `${propio ? `${propio.canonicalOrigin}/api/v1` : API_BASE}/auth/federated/start?idp=${idp}&volver=${encodeURIComponent(volver)}`;
  };
  const done = async () => {
    avisarIngreso();
    const propio = await entityFromHost();
    if (propio) {
      // La marca de «acaba de ingresar» no viaja entre hosts: va en la URL (`?ingreso=1`), y el Inicio la lee.
      const u = new URL(volver, window.location.origin);
      if (alInicio) u.searchParams.set('ingreso', '1');
      const { url } = await apiPost<{ url: string }>('/auth/handoff', { returnTo: u.pathname + u.search });
      window.location.replace(url);
      return;
    }
    markEntry();
    await qc.invalidateQueries({ queryKey: ['me'] });
    navigate(volver, { replace: true });
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaToken) await apiPost('/auth/login/mfa', { mfaToken, code });
      else if (providers?.local === false) {
        // Sin ingreso local solo queda el proveedor de la entidad del correo.
        const value = email.trim().toLowerCase();
        const p = await qc.fetchQuery({ queryKey: ['providers', value], queryFn: () => apiGet<Providers>('/auth/providers', { email: value }) });
        if (p.federated) return await startFederated(p.federated.id);
        setError('Tu correo no pertenece a una entidad con ingreso corporativo.');
        return;
      } else {
        const r = await apiPost<{ mfaRequired?: boolean; mfaToken?: string }>('/auth/login', { email, password });
        if (r.mfaRequired && r.mfaToken) {
          setMfaToken(r.mfaToken);
          return;
        }
      }
      await done();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  if (mfaToken) {
    return marco({
      titulo: 'Verificación en dos pasos',
      subtitulo: 'Escribe el código de 6 dígitos de tu app autenticadora (o uno de recuperación).',
      children: (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertDescription>{error}</AlertDescription></Alert>}
          <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
          </InputOTP>
          <Collapsible><CollapsibleTrigger asChild><Button type="button" variant="link" size="sm" className="px-0">Usar un código de recuperación</Button></CollapsibleTrigger><CollapsibleContent><Input className="mt-2" value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXX-XXXX" aria-label="Código de recuperación" /></CollapsibleContent></Collapsible>
          <Button type="submit" className="w-full" disabled={busy || code.length < 6}>{busy ? 'Verificando…' : 'Verificar'}</Button>
        </form>
      ),
    });
  }
  if (olvide) {
    return marco({
      titulo: 'Restablecer contraseña',
      subtitulo: 'Te enviaremos un enlace que sirve una sola vez.',
      children: (
        <>
          {enviado ? <Alert variant="success"><Icono nombre="mdi:email-outline" /><AlertDescription>Si el correo tiene cuenta, te llegará el enlace en unos minutos. Si no te llega, pídele al administrador de tu entidad un enlace para restablecer la contraseña.</AlertDescription></Alert> : (
            <form onSubmit={(e) => { e.preventDefault(); void apiPost('/auth/password/forgot', { email }).finally(() => setEnviado(true)); }} className="space-y-4">
              <Field><FieldLabel htmlFor="femail">Correo</FieldLabel><Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
              <Button type="submit" className="w-full">Enviar enlace</Button>
            </form>
          )}
          <Button type="button" variant="link" className="mt-2 w-full" onClick={() => { setOlvide(false); setEnviado(false); }}>Volver al ingreso</Button>
        </>
      ),
    });
  }
  const federated = checked && email.trim().toLowerCase() === checked ? providers?.federated : null;
  const local = providers?.local !== false;
  return marco({
    titulo,
    subtitulo,
    children: (
      <form onSubmit={submit} className="space-y-4">
        {aviso}
        {error && <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertDescription>{error}</AlertDescription></Alert>}
        <FieldGroup>
          <Field><FieldLabel htmlFor="email">Correo</FieldLabel><Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} onBlur={checkEmail} autoFocus /></Field>
          {federated && <Button type="button" variant="outline" className="w-full" onClick={() => void startFederated(federated.id)}><Icono nombre="mdi:domain" />Ingresar con {federated.name} ({federated.entity})</Button>}
          {local && (
            <Field><FieldLabel htmlFor="password">Contraseña</FieldLabel><InputClave id="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
          )}
          <Button type="submit" className="w-full" disabled={busy}>{busy ? 'Ingresando…' : local ? 'Ingresar' : 'Continuar'}</Button>
        </FieldGroup>
        <Button type="button" variant="link" className="w-full" onClick={() => setOlvide(true)}>¿Olvidaste tu contraseña?</Button>
      </form>
    ),
  });
}

